"use client"

import { useState } from "react"
import type { ReactNode } from "react"
import { createClient } from "@/lib/supabase/client"
import { Browser } from "@capacitor/browser"
import { isNativeApp, NATIVE_AUTH_CALLBACK } from "@/lib/native"
import { signInWithAppleNative } from "@/lib/auth/apple-native"
import { IN_APP_GOOGLE_MESSAGE, isInAppBrowser } from "@/lib/in-app-browser"

/**
 * One OAuth button, whichever provider.
 *
 * On the web both providers take the same Supabase redirect. In the native
 * shell they diverge, because the two companies want opposite things:
 *
 *   Google  — refuses to authenticate in a web view ("disallowed_useragent"),
 *             so the consent screen opens in the system browser and comes back
 *             on the co.boothapp.app:// scheme.
 *   Apple   — expects its own system sheet on iOS. lib/auth/apple-native runs
 *             ASAuthorizationController through the plugin and exchanges the
 *             identity token for a session without ever leaving the app.
 */

export type OAuthProvider = "google" | "apple"

type OAuthButtonProps = {
  provider: OAuthProvider
  icon: ReactNode
  label: string
  /** Shown if the OAuth redirect fails before leaving the page. */
  errorFallback: string
  /** Carried through the OAuth redirect so /auth/callback can assign it on first sign-in. */
  role?: "barber" | "owner" | "client"
  /** Persisted to localStorage before redirecting so the invite survives the OAuth round-trip. */
  inviteToken?: string | null
  /** Where to land after sign-in (sanitized again by completeOAuthSignIn). */
  redirect?: string | null
  onError?: (message: string) => void
  className?: string
}

function nativeLanding(role: string | undefined, redirect: string | null | undefined): string {
  const params = new URLSearchParams()
  if (role) params.set("role", role)
  if (redirect) params.set("redirect", redirect)
  const qs = params.toString()
  return qs ? `/auth/native?${qs}` : "/auth/native"
}

export function OAuthButton({
  provider,
  icon,
  label,
  errorFallback,
  role,
  inviteToken,
  redirect,
  onError,
  className = "",
}: OAuthButtonProps) {
  const [loading, setLoading] = useState(false)

  async function handleClick() {
    setLoading(true)
    try {
      if (inviteToken) {
        localStorage.setItem("booth_pending_invite", inviteToken)
      }

      const supabase = createClient()

      // Inside the iOS/Android shell Google refuses to run OAuth in a web view
      // ("disallowed_useragent"), so the consent screen opens in the system
      // browser and Supabase hands the code back on our custom scheme; the
      // NativeShell listener exchanges it in the web view's own session.
      // Apple takes the same path: the web view is not a browser it will
      // authenticate in either, and one round-trip is one thing to maintain.
      // Apple's native sheet. No browser, no round-trip: the session is set
      // here and /auth/native picks it up from the cookie on the next request.
      if (isNativeApp() && provider === "apple") {
        const result = await signInWithAppleNative(supabase)
        if (!result.ok) {
          // A cancel is a decision, not a failure — say nothing and let them
          // tap something else.
          if (!result.cancelled) onError?.(result.message || errorFallback)
          setLoading(false)
          return
        }
        // Full navigation, not a client transition: /auth/native is a route
        // handler and needs the fresh session cookie on the request.
        window.location.assign(nativeLanding(role, redirect))
        return
      }

      if (isNativeApp()) {
        const redirectTo = new URL(NATIVE_AUTH_CALLBACK)
        // After the exchange NativeShell lands here, where the role bootstrap
        // the browser callback does happens server-side.
        redirectTo.searchParams.set("next", nativeLanding(role, redirect))
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: { redirectTo: redirectTo.toString(), skipBrowserRedirect: true },
        })
        if (error || !data?.url) {
          onError?.(error?.message || errorFallback)
          setLoading(false)
          return
        }
        await Browser.open({ url: data.url, presentationStyle: "popover" })
        setLoading(false)
        return
      }

      // Instagram's (or Facebook's, TikTok's) built-in browser: Google won't
      // sign in there, and finishing in Safari can't work (lib/auth/pkce.ts).
      // Say how to get out instead of starting a sign-in that will fail.
      if (provider === "google" && isInAppBrowser()) {
        onError?.(IN_APP_GOOGLE_MESSAGE)
        setLoading(false)
        return
      }

      const redirectTo = new URL("/auth/callback", window.location.origin)
      if (role) redirectTo.searchParams.set("role", role)
      if (redirect) redirectTo.searchParams.set("redirect", redirect)

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: redirectTo.toString() },
      })

      // On success the browser navigates to the provider — we only get here on failure.
      if (error) {
        onError?.(error.message || errorFallback)
        setLoading(false)
      }
    } catch {
      onError?.(errorFallback)
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`flex h-10 w-full items-center justify-center gap-2.5 rounded-full border-[0.6px] border-[#161616] bg-white/[0.03] text-xs font-medium text-white shadow-sm transition-colors hover:bg-white/[0.06] disabled:opacity-60 outline-none focus-visible:ring-2 focus-visible:ring-white/15 ${className}`}
    >
      {icon}
      {label}
    </button>
  )
}
